import React from 'react';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { BsWhatsapp } from 'react-icons/bs';
import PhoneIcon from '@mui/icons-material/Phone';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import seoConfig from '../../config/seo/seoConfig';
import './CTAButton.css';

const CTAButton = ({
  text,
  type = 'link',
  href,
  message,
  variant = 'primary',
  size = 'medium',
  icon: CustomIcon,
  fullWidth = false,
  onClick
}) => {
  // Get WhatsApp number from config (remove non-digits)
  const whatsappNumber = seoConfig.company.whatsapp.replace(/\D/g, '');

  // Generate WhatsApp URL
  const getWhatsAppURL = () => {
    const encodedMessage = encodeURIComponent(message || 'Olá! Gostaria de mais informações.');
    return `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
  };

  // Get button icon based on type
  const getIcon = () => {
    if (CustomIcon) return <CustomIcon />;
    
    switch (type) {
      case 'whatsapp':
        return <BsWhatsapp style={{ fontSize: '1.2em', marginRight: '8px' }} />;
      case 'phone':
        return <PhoneIcon sx={{ mr: 1 }} />;
      case 'external':
        return <OpenInNewIcon sx={{ mr: 1 }} />;
      default:
        return null;
    }
  };

  // Get button styles based on variant
  const getButtonStyles = () => {
    const baseStyles = {
      fontFamily: '"Reem Kufi", sans-serif',
      fontWeight: 600,
      textTransform: 'none',
      borderRadius: '8px',
      px: { xs: 2.5, md: 3 },
      py: { xs: 1, md: 1.25 },
      fontSize: {
        xs: size === 'small' ? '0.875rem' : size === 'large' ? '1.1rem' : '1rem',
        md: size === 'small' ? '0.95rem' : size === 'large' ? '1.25rem' : '1.1rem'
      },
      transition: 'all 0.3s ease',
      boxShadow: 2,
      '&:hover': {
        boxShadow: 4,
        transform: 'translateY(-2px)'
      }
    };

    switch (variant) {
      case 'primary':
        return {
          ...baseStyles,
          backgroundColor: '#1B422E',
          color: '#fff',
          '&:hover': {
            ...baseStyles['&:hover'],
            backgroundColor: '#143320'
          }
        };
      case 'secondary':
        return {
          ...baseStyles,
          backgroundColor: '#FE9E0D',
          color: '#fff',
          '&:hover': {
            ...baseStyles['&:hover'],
            backgroundColor: '#e58d0b'
          }
        };
      case 'whatsapp':
        return {
          ...baseStyles,
          backgroundColor: '#25D366',
          color: '#fff',
          '&:hover': {
            ...baseStyles['&:hover'],
            backgroundColor: '#1fbd58'
          }
        };
      case 'outlined':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: '#1B422E',
          border: '2px solid #1B422E',
          boxShadow: 0,
          '&:hover': {
            backgroundColor: '#1B422E',
            color: '#fff',
            boxShadow: 2,
            transform: 'translateY(-2px)'
          }
        };
      default:
        return baseStyles;
    }
  };

  // Render button based on type
  const renderButton = () => {
    const buttonContent = (
      <>
        {getIcon()}
        {text}
      </>
    );

    const buttonProps = {
      className: 'cta-button',
      sx: getButtonStyles(),
      fullWidth: fullWidth,
      onClick: onClick
    };

    switch (type) {
      case 'whatsapp':
        return (
          <Button
            {...buttonProps}
            component="a"
            href={getWhatsAppURL()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {buttonContent}
          </Button>
        );

      case 'phone':
        return (
          <Button
            {...buttonProps}
            component="a"
            href={`tel:${href}`}
          >
            {buttonContent}
          </Button>
        );

      case 'external':
        return (
          <Button
            {...buttonProps}
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {buttonContent}
          </Button>
        );

      case 'link':
        return (
          <Button
            {...buttonProps}
            component={RouterLink}
            to={href || '/'}
          >
            {buttonContent}
          </Button>
        );

      default:
        return (
          <Button {...buttonProps}>
            {buttonContent}
          </Button>
        );
    }
  };

  return renderButton();
};

export default CTAButton;
